import { LayoutConfig, layoutConfig, pageLevelDefaultProps } from '../sampleConfig';
import { Section } from '../components/sections/TextSection';
import { EventEmitter, EVENTS } from './EventEmitter';
import { IStateChangeInfo, UndoManager } from '../managers/UndoManager';
import { LayoutManager } from '../LayoutManager';
import { DomHelper } from '../utlis/DomHelper';

export const changeType = {
  ADD_SECTION: "addSection",
  REMOVE_SECTION: "removeSection",
  UPDATE_SECTION: "updateSection",
  UPDATE_RESIZE_INFO: "updateResizeInfo",
  SELECTION_CHANGE: "selectionChanged",
  UPDATED_VIEWPORT_CONFIG: "updatedViewportConfig",
  UPDATE_ALL_Section: "updatedAllSection",
  SECTION_PASTED: "sectionPasted",
  BRING_TO_FRONT: "BringToFront",
  SEND_TO_BACK: "SendToBack",
  UPDATE_PAGE_PROP: "updatePageProperties",
};

export class StateManager {
  private static instance: StateManager;
  private zoomValue:number;
  private cellUnitSize:number;
  private state: LayoutConfig;
  private multiplier:number;
  private eventEmitter: EventEmitter<any>;
  private undoManager: UndoManager;
  private threshHoldMargin:number
  private notAllowedActionsForUndo:Set<string>= new Set([changeType.SELECTION_CHANGE,changeType.UPDATED_VIEWPORT_CONFIG])
  private constructor() {
    this.state = {
      propInfo: [...pageLevelDefaultProps],
      sections:[],
      height: null,
      width: null,
      rowCount: 150,
      colCount:  70,
      cellWidth: -1,
      cellHeight: -1,
      defaultScreenSize:{label:"Mobile",width:15,height:7,unit:1},
      scale:[1,1]
    };
    this.threshHoldMargin = 10;
    this.cellUnitSize = 1;
    this.zoomValue = 1;
    this.multiplier = 1;
    this.eventEmitter = EventEmitter.getInstance();
    this.undoManager = UndoManager.getInstance();
  }
  private getExponentToBringUnder100(val:number,threshHold:number):number{
    if(Math.abs(val - threshHold) < this.threshHoldMargin)
      return 0
    if(Math.abs((val * 2) - threshHold) < this.threshHoldMargin)
      return - 1
    else if(val < threshHold){
      if((val * 2) < threshHold)
        return (this.getExponentToBringUnder100(val * 2,threshHold) - 1)
      else
        return 0
    }
    else {
      return (this.getExponentToBringUnder100(val/2,threshHold) + 1)
    }
      
  }
private updateMultiplier(){
  
  if(this.state.defaultScreenSize){
    if(this.state.colCount >= this.state.rowCount){
      let exp = this.getExponentToBringUnder100(this.state.colCount, this.state.defaultScreenSize.width * 10);
      this.multiplier = Math.pow(2,exp);
    }
    else{
      let exp = this.getExponentToBringUnder100(this.state.rowCount, this.state.defaultScreenSize.height * 10);
      this.multiplier = Math.pow(2,exp);
    }
  }
  else{
    this.multiplier = 1;
  }  
  this.state.defaultScreenSize = {label:"Mobile",width:this.state.colCount/(10 * this.multiplier),height:this.state.rowCount/(10 * this.multiplier),unit:1}
    
}
public static getInstance(): StateManager {
    if (!StateManager.instance) {
      StateManager.instance = new StateManager();
    }
    return StateManager.instance;
  }
  public getState(): LayoutConfig {
    return JSON.parse(JSON.stringify(this.state)) as LayoutConfig;
  }
  public setState(stateChangeInfo:IStateChangeInfo): void {
    if(!this.notAllowedActionsForUndo.has(stateChangeInfo.changeType)){
        this.undoManager.pushState(stateChangeInfo);
    }
    this.state = { ...this.state, ...stateChangeInfo.state };
    if(stateChangeInfo.zoomValue)
      this.setZoomValue(stateChangeInfo.zoomValue)
    this.updateMultiplier();
    this.eventEmitter.emit(EVENTS.STATE_UPDATED, stateChangeInfo);
  }
  
  public getMultipler():number{
    return this.multiplier;
  }
  public getZoomValue():number{
    return this.zoomValue;
  }
  public setZoomValue(val:number){
    this.zoomValue = val;
  }
  resetState(): void {
    this.state = {
      propInfo: [...pageLevelDefaultProps],
      sections:[],
      height: null,
      width: null,
      rowCount: 150,
      colCount:  70,
      cellWidth: -1,
      cellHeight: -1,
      defaultScreenSize:{label:"Mobile",width:15,height:7,unit:1},
      scale:[1,1]
    };
    this.zoomValue = 1;
    this.multiplier = 1;
    this.setState({state:this.state,changeType:changeType.UPDATE_ALL_Section});
    LayoutManager.syncStateToDOM({state:this.state,changeType:changeType.UPDATE_ALL_Section});
    this.eventEmitter.emit(EVENTS.STATE_UPDATED, {state:this.state,changeType:changeType.UPDATE_ALL_Section});
  }

  public getSelectedSections(): Section[] {
    return DomHelper.cloneObj(this.state.sections.filter((section: Section) => section.selected));
  }
  public getIndexOfSection(section:Section):number{     
    for(let i:number = 0;i<this.state.sections.length;i++){
      if(section.id == this.state.sections[i].id){
        return i;
      }
    }
    return -1;
  }

  public subscribe(event: string, callback: (data: any) => void): void {
    this.eventEmitter.subscribe(event, callback);
  }

  public unsubscribe(event: string, callback: (data: any) => void): void {
    this.eventEmitter.unsubscribe(event, callback);
  }

  public undo(): void {
    const previousState = this.undoManager.undo();
    let newState = this.undoManager.peekUndo();
    if(!newState) {
      // newState will be default state
      newState = { state: {...this.toJSON(),sections:[]}, zoomValue: this.zoomValue, multiplier:this.multiplier, changeType: ''};
    }
      LayoutManager.syncStateToDOM(newState);
      this.state = { ...this.state, ...newState.state };
      if(newState.zoomValue)
        this.zoomValue = newState.zoomValue;
      if(newState.multiplier)
        this.multiplier = newState.multiplier;
      this.eventEmitter.emit(EVENTS.STATE_UPDATED, newState);
  }
  
  public redo(): void {
    const nextState = this.undoManager.redo();
    if (nextState) {
      LayoutManager.syncStateToDOM(nextState);
      this.state = { ...this.state, ...nextState.state };
      if(nextState.zoomValue)
        this.zoomValue = nextState.zoomValue;
      if(nextState.multiplier)
        this.multiplier = nextState.multiplier;
      this.eventEmitter.emit(EVENTS.STATE_UPDATED, nextState);
    }
  }
  public toJSON(){
    return JSON.parse(JSON.stringify(this.state))
  }

  public canUndo(): boolean {
    return this.undoManager.canUndo();
  }

  public canRedo(): boolean {
    return this.undoManager.canRedo();
  }
}
